import React, { useState, useEffect, useCallback } from 'react';
import {
  User,
  Mail,
  Phone,
  GraduationCap,
  MapPin,
  Lock,
  Save,
  X,
  RefreshCw,
  AlertCircle,
  FileText,
  FileDown,
  Stethoscope,
} from 'lucide-react';
import {
  colaboradoresService,
  type ColaboradorFicha,
  type UpdateColaboradorDto,
} from '../../services/colaboradores.service';
import { resultadosService, type Resultado } from '../../services/resultados.service';
import { DomainService, type Formacao } from '../../services/domain.service';
import { type EnderecoViaCep } from '../../services/cep.service';
import { formatCPF, formatPhone, unformat } from '../../utils/formatters';
import { formatDateBR } from '../../utils/dateUtils';
import StateSelect from '../StateSelect';
import CepInput from '../CepInput';

export interface ColaboradorFichaAccordionProps {
  colaboradorId: number;
  cargoNome?: string;
  setorNome?: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function ColaboradorFichaAccordion({
  colaboradorId,
  cargoNome,
  setorNome,
  onClose,
  onSaved,
}: ColaboradorFichaAccordionProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Dados originais
  const [ficha, setFicha] = useState<ColaboradorFicha | null>(null);
  const [formacoes, setFormacoes] = useState<Formacao[]>([]);

  // Campos editáveis
  const [nomeSocial, setNomeSocial] = useState('');
  const [nomeMae, setNomeMae] = useState('');
  const [pcd, setPcd] = useState(false);
  const [enviarEsocial, setEnviarEsocial] = useState(true);
  const [regimeContratacao, setRegimeContratacao] = useState('');
  const [estadoCivil, setEstadoCivil] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [matriculaEsocial, setMatriculaEsocial] = useState('');
  const [formacaoAcademicaId, setFormacaoAcademicaId] = useState<number | ''>('');

  // Endereço editável
  const [endereco, setEndereco] = useState({
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    municipio: '',
    uf: '',
  });

  // Exames complementares
  const [resultados, setResultados] = useState<Resultado[]>([]);
  const [downloadingResultadoId, setDownloadingResultadoId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const [fichaData, formacoesData, resultadosData] = await Promise.all([
        colaboradoresService.getFicha(colaboradorId),
        DomainService.getFormacoes().catch(() => [] as Formacao[]),
        resultadosService.getResultadosPorColaborador(colaboradorId).catch(() => ({ data: [] })),
      ]);

      setFicha(fichaData);
      setFormacoes(formacoesData || []);
      setResultados(resultadosData?.data || []);

      setNomeSocial(fichaData.nome_social || fichaData.nome || '');
      setNomeMae(fichaData.nome_mae || '');
      setPcd(Boolean(fichaData.pcd));
      setEnviarEsocial(fichaData.enviar_esocial !== false);
      setRegimeContratacao(fichaData.regime_contratacao || '');
      setEstadoCivil(fichaData.estado_civil || '');
      setEmail(fichaData.email || '');
      setTelefone(fichaData.telefone ? formatPhone(fichaData.telefone) : '');
      setMatriculaEsocial(fichaData.matricula_esocial || '');
      setFormacaoAcademicaId(fichaData.formacao_academica_id || '');

      if (fichaData.endereco) {
        setEndereco({
          cep: fichaData.endereco.cep || '',
          logradouro: fichaData.endereco.logradouro || '',
          numero: fichaData.endereco.numero || '',
          complemento: fichaData.endereco.complemento || '',
          bairro: fichaData.endereco.bairro || '',
          municipio: fichaData.endereco.municipio || fichaData.endereco.cidade || '',
          uf: fichaData.endereco.uf || fichaData.endereco.estado || '',
        });
      }
    } catch (err: unknown) {
      console.error('Erro ao carregar ficha do colaborador:', err);
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = axiosErr.response?.data?.message || axiosErr.message || 'Não foi possível carregar os dados cadastrais do colaborador.';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  }, [colaboradorId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Formatação em tempo real do telefone
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, '').slice(0, 11);
    let formatted = digits;
    if (digits.length > 2) {
      if (digits.length <= 6) {
        formatted = `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      } else if (digits.length <= 10) {
        formatted = `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
      } else {
        formatted = `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
      }
    }
    setTelefone(formatted);
  };

  // Preenchimento de endereço retornado pelo CepInput
  const handleAddressFound = (viaCep: EnderecoViaCep) => {
    setEndereco((prev) => ({
      ...prev,
      logradouro: viaCep.logradouro || prev.logradouro,
      bairro: viaCep.bairro || prev.bairro,
      municipio: viaCep.localidade || prev.municipio,
      uf: viaCep.uf || prev.uf,
    }));
  };

  const [downloadingAso, setDownloadingAso] = useState(false);

  const handleDownloadAso = async () => {
    if (!ficha?.aso) {
      alert('Arquivo do ASO não disponível para download.');
      return;
    }
    try {
      setDownloadingAso(true);
      let blob: Blob;
      if (ficha.aso.resultado_id) {
        try {
          blob = await resultadosService.downloadResultado(ficha.aso.resultado_id);
        } catch {
          blob = await colaboradoresService.downloadColaboradorAso(colaboradorId);
        }
      } else {
        blob = await colaboradoresService.downloadColaboradorAso(colaboradorId);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cleanName = (ficha.nome || 'colaborador').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `ASO_${cleanName}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: unknown) {
      console.error('Erro ao baixar ASO:', err);
      alert('Não foi possível realizar o download do ASO no momento.');
    } finally {
      setDownloadingAso(false);
    }
  };

  const handleDownloadResultado = async (resultadoId: number, exameNome: string) => {
    try {
      setDownloadingResultadoId(resultadoId);
      const blob = await resultadosService.downloadResultado(resultadoId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cleanExame = exameNome.replace(/[^a-zA-Z0-9]/g, '_');
      const cleanColab = (ficha?.nome || 'colaborador').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `Laudo_${cleanExame}_${cleanColab}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: unknown) {
      console.error('Erro ao baixar resultado:', err);
      alert('Não foi possível realizar o download do laudo no momento.');
    } finally {
      setDownloadingResultadoId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrorMessage('O campo E-mail é obrigatório.');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage(null);

      const cleanPhone = unformat(telefone);
      const cleanCep = unformat(endereco.cep);

      const payload: UpdateColaboradorDto = {
        nome_social: nomeSocial.trim() || undefined,
        nome_mae: nomeMae.trim() || undefined,
        pcd,
        enviar_esocial: enviarEsocial,
        regime_contratacao: regimeContratacao.trim() || undefined,
        estado_civil: estadoCivil.trim() || undefined,
        email: email.trim(),
        telefone: cleanPhone || undefined,
        matricula_esocial: matriculaEsocial.trim() || undefined,
        formacao_academica_id: formacaoAcademicaId ? Number(formacaoAcademicaId) : null,
        endereco: {
          cep: cleanCep,
          logradouro: endereco.logradouro.trim(),
          numero: endereco.numero.trim(),
          complemento: endereco.complemento.trim(),
          bairro: endereco.bairro.trim(),
          municipio: endereco.municipio.trim(),
          uf: endereco.uf.trim(),
        },
      };

      await colaboradoresService.update(colaboradorId, payload);
      onSaved();
    } catch (err: unknown) {
      console.error('Erro ao atualizar colaborador:', err);
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      const msg = axiosErr.response?.data?.message || axiosErr.message || 'Erro ao salvar alterações do colaborador.';
      setErrorMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-50 border border-primary-200 rounded-xl p-6 shadow-sm flex items-center justify-center space-x-3 text-slate-600 my-2">
        <RefreshCw className="w-5 h-5 text-primary-600 animate-spin" />
        <span className="text-xs font-medium">Carregando ficha cadastral do colaborador...</span>
      </div>
    );
  }

  const isDesligado =
    ficha?.status === 'desligado' ||
    Boolean(ficha?.data_demissao) ||
    ficha?.enabled === false;

  const isCandidato =
    ficha?.status === 'candidato' ||
    Boolean(ficha?.is_candidato);

  return (
    <div className="bg-white border-2 border-primary-200 rounded-xl p-5 shadow-sm space-y-5 my-2.5 transition-all">
      {/* Header do Accordion */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-3">
        <div className="flex items-center space-x-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary-100 text-primary-800 flex items-center justify-center font-bold text-xs">
            <User className="w-4 h-4" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <span>Ficha Cadastral: {ficha?.nome}</span>
              <span className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-full">
                Edição
              </span>
              {isDesligado && (
                <span className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-full">
                  Desligado
                </span>
              )}
              {!isDesligado && isCandidato && (
                <span className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-full">
                  Candidato
                </span>
              )}
            </h4>
            <p className="text-xs text-gray-500">
              Atualize as informações cadastrais e de contato do colaborador.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {ficha?.aso && (
            <button
              type="button"
              onClick={handleDownloadAso}
              disabled={downloadingAso}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
              title={`Baixar ASO digital (${ficha.aso.valor || 'Laudo emitido'}${ficha.aso.data ? ` - ${formatDateBR(ficha.aso.data)}` : ''})`}
            >
              <FileDown className="w-4 h-4 text-emerald-600" />
              <span>{downloadingAso ? 'Baixando...' : 'Baixar ASO'}</span>
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            title="Fechar formulário"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Alerta de erro se houver */}
      {errorMessage && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-lg text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Seção 1: Dados Institucionais e Estruturais (Somente Leitura) */}
      <div className="bg-slate-50 border border-slate-200/90 rounded-xl p-3.5 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-xs font-semibold text-slate-700">
            <Lock className="w-3.5 h-3.5 text-slate-400" />
            <span>Dados Funcionais e Estruturais (Somente Leitura)</span>
          </div>
          <span className="text-[10px] text-slate-500 italic">
            Cargo e setor vinculados ao organograma
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-xs">
          <div>
            <span className="text-slate-400 block text-[11px]">CPF</span>
            <span className="font-semibold text-slate-800">{formatCPF(ficha?.cpf)}</span>
          </div>
          <div>
            <span className="text-slate-400 block text-[11px]">RG</span>
            <span className="font-medium text-slate-800">{ficha?.rg || 'Não informado'}</span>
          </div>
          <div>
            <span className="text-slate-400 block text-[11px]">Matrícula eSocial</span>
            <span className="font-semibold text-indigo-700">
              {ficha?.matricula_esocial || 'Não informada'}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block text-[11px]">Nome da Mãe</span>
            <span className="font-medium text-slate-800">
              {ficha?.nome_mae || 'Não informado'}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block text-[11px]">Data de Nascimento</span>
            <span className="font-medium text-slate-800">
              {ficha?.data_nascimento ? formatDateBR(ficha.data_nascimento) : 'Não informada'}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block text-[11px]">Sexo</span>
            <span className="font-medium text-slate-800">
              {ficha?.sexo === 'M' ? 'Masculino' : ficha?.sexo === 'F' ? 'Feminino' : 'Não informado'}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block text-[11px]">Cargo Atual</span>
            <span className="font-semibold text-primary-800">
              {ficha?.cargo?.nome || cargoNome || '-'}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block text-[11px]">Setor Atual</span>
            <span className="font-semibold text-primary-800">
              {ficha?.setor?.nome || setorNome || '-'}
            </span>
          </div>
          <div>
            <span className="text-slate-400 block text-[11px]">Data de Admissão</span>
            <span className="font-medium text-slate-800">
              {ficha?.data_admissao ? formatDateBR(ficha.data_admissao) : 'Não informada'}
            </span>
          </div>
          {isDesligado && (
            <div>
              <span className="text-slate-400 block text-[11px]">Data de Demissão</span>
              <span className="font-semibold text-rose-700">
                {ficha?.data_demissao ? formatDateBR(ficha.data_demissao) : 'Não informada'}
              </span>
            </div>
          )}
          <div>
            <span className="text-slate-400 block text-[11px]">Status</span>
            {isDesligado ? (
              <span className="inline-flex items-center gap-1 font-semibold text-rose-700">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                Desligado
              </span>
            ) : isCandidato ? (
              <span className="inline-flex items-center gap-1 font-semibold text-purple-700">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                Candidato
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Ativo
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Seção 2: Laudos e Exames Complementares (Com Scroll Seguro) */}
      <div className="bg-slate-50 border border-slate-200/90 rounded-xl p-3.5 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-xs font-semibold text-slate-700">
            <Stethoscope className="w-3.5 h-3.5 text-primary-600" />
            <span>Exames Complementares & Laudos ({resultados.length})</span>
          </div>
          <span className="text-[10px] text-slate-500">
            {resultados.length > 0 ? 'Últimos laudos emitidos' : 'Nenhum exame complementar'}
          </span>
        </div>

        {resultados.length === 0 ? (
          <div className="text-center py-4 bg-white rounded-lg border border-dashed border-gray-200">
            <p className="text-xs text-gray-500">
              Nenhum laudo ou exame complementar registrado para este colaborador.
            </p>
          </div>
        ) : (
          <div className="max-h-56 overflow-y-auto pr-1 space-y-2">
            {resultados.map((res) => (
              <div
                key={res.id}
                className="bg-white border border-gray-200 rounded-lg p-2.5 flex items-center justify-between gap-3 text-xs hover:border-gray-300 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 truncate">{res.exame}</span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${
                        res.status === 'aprovado'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : res.status === 'laudo_emitido'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : res.status === 'reprovado'
                          ? 'bg-rose-50 text-rose-700 border border-rose-200'
                          : 'bg-gray-100 text-gray-600 border border-gray-200'
                      }`}
                    >
                      {res.status === 'aprovado'
                        ? 'Aprovado'
                        : res.status === 'laudo_emitido'
                        ? 'Laudo Emitido'
                        : res.status === 'reprovado'
                        ? 'Reprovado'
                        : 'Pendente'}
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {res.data_realizacao && (
                      <span>Realização: {formatDateBR(res.data_realizacao)}</span>
                    )}
                    {res.clinica && <span>Clínica: {res.clinica}</span>}
                    {res.medico && (
                      <span>
                        Médico: {res.medico} {res.crm ? `(${res.crm})` : ''}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => handleDownloadResultado(res.id, res.exame)}
                    disabled={downloadingResultadoId === res.id}
                    title="Baixar Laudo em PDF"
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0"
                  >
                    {downloadingResultadoId === res.id ? (
                      <RefreshCw className="w-3 h-3 animate-spin text-primary-600" />
                    ) : (
                      <FileDown className="w-3 h-3 text-primary-600" />
                    )}
                    <span>Baixar Laudo</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulário de Edição */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Bloco: Identificação, PCD & e-Social */}
        <div>
          <h5 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5 text-primary-600" />
            <span>Identificação & e-Social</span>
          </h5>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3.5">
            {/* Nome Social */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Nome Social
              </label>
              <input
                type="text"
                value={nomeSocial}
                onChange={(e) => setNomeSocial(e.target.value)}
                placeholder="Nome social preferencial"
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            {/* Nome da Mãe */}
            <div className="col-span-1 sm:col-span-3">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Nome Completo da Mãe
              </label>
              <input
                type="text"
                value={nomeMae}
                onChange={(e) => setNomeMae(e.target.value)}
                placeholder="Nome da mãe do colaborador"
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>

            {/* PCD */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                PCD (Deficiência)
              </label>
              <select
                value={pcd ? 'true' : 'false'}
                onChange={(e) => setPcd(e.target.value === 'true')}
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="false">Não</option>
                <option value="true">Sim</option>
              </select>
            </div>

            {/* Enviar ao e-Social */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Enviar ao e-Social
              </label>
              <select
                value={enviarEsocial ? 'true' : 'false'}
                onChange={(e) => setEnviarEsocial(e.target.value === 'true')}
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="true">Sim (Padrão)</option>
                <option value="false">Não</option>
              </select>
            </div>

            {/* Regime de Contratação */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Regime Contratação
              </label>
              <select
                value={regimeContratacao}
                onChange={(e) => setRegimeContratacao(e.target.value)}
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              >
                <option value="">Selecione...</option>
                <option value="CLT">CLT</option>
                <option value="PJ">PJ</option>
                <option value="Estágio">Estágio</option>
                <option value="Menor Aprendiz">Menor Aprendiz</option>
                <option value="Autônomo">Autônomo</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bloco: Contato e Formação */}
        <div className="border-t border-gray-200 pt-4">
          <h5 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5 text-primary-600" />
            <span>Contato & Formação</span>
          </h5>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
            {/* E-mail */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                E-mail <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="exemplo@empresa.com.br"
                  required
                  className="w-full text-xs px-3 py-2 pl-8 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <Mail className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Telefone */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Telefone / Celular
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={telefone}
                  onChange={handlePhoneChange}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                  className="w-full text-xs px-3 py-2 pl-8 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <Phone className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Matrícula eSocial */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Matrícula eSocial
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={matriculaEsocial}
                  onChange={(e) => setMatriculaEsocial(e.target.value)}
                  placeholder="000123"
                  className="w-full text-xs px-3 py-2 pl-8 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <FileText className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Formação Acadêmica */}
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Formação Acadêmica
              </label>
              <div className="relative">
                <select
                  value={formacaoAcademicaId}
                  onChange={(e) => setFormacaoAcademicaId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full text-xs px-3 py-2 pl-8 border border-gray-300 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                >
                  <option value="">Selecione a formação...</option>
                  {formacoes.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.descricao || f.valor}
                    </option>
                  ))}
                </select>
                <GraduationCap className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Bloco: Endereço Residencial */}
        <div className="border-t border-gray-200 pt-4">
          <h5 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-primary-600" />
            <span>Endereço Residencial</span>
          </h5>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3.5">
            {/* CEP */}
            <div className="col-span-1 sm:col-span-2 md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                CEP
              </label>
              <CepInput
                value={endereco.cep}
                onChange={(val) => setEndereco((prev) => ({ ...prev, cep: val }))}
                onAddressFound={handleAddressFound}
                className="text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Logradouro */}
            <div className="col-span-1 sm:col-span-2 md:col-span-3">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Logradouro
              </label>
              <input
                type="text"
                value={endereco.logradouro}
                onChange={(e) => setEndereco((prev) => ({ ...prev, logradouro: e.target.value }))}
                placeholder="Rua, Avenida, etc."
                maxLength={255}
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Número */}
            <div className="col-span-1 sm:col-span-1 md:col-span-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Número
              </label>
              <input
                type="text"
                value={endereco.numero}
                onChange={(e) => setEndereco((prev) => ({ ...prev, numero: e.target.value }))}
                placeholder="123"
                maxLength={10}
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Complemento */}
            <div className="col-span-1 sm:col-span-1 md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Complemento
              </label>
              <input
                type="text"
                value={endereco.complemento}
                onChange={(e) => setEndereco((prev) => ({ ...prev, complemento: e.target.value }))}
                placeholder="Apto, Sala, Bloco"
                maxLength={255}
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Bairro */}
            <div className="col-span-1 sm:col-span-1 md:col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Bairro
              </label>
              <input
                type="text"
                value={endereco.bairro}
                onChange={(e) => setEndereco((prev) => ({ ...prev, bairro: e.target.value }))}
                placeholder="Nome do bairro"
                maxLength={255}
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Município / Cidade */}
            <div className="col-span-1 sm:col-span-1 md:col-span-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Cidade
              </label>
              <input
                type="text"
                value={endereco.municipio}
                onChange={(e) => setEndereco((prev) => ({ ...prev, municipio: e.target.value }))}
                placeholder="Cidade"
                maxLength={50}
                className="w-full text-xs px-3 py-2 border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Estado (UF) */}
            <div className="col-span-1 sm:col-span-1 md:col-span-1">
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                UF
              </label>
              <StateSelect
                value={endereco.uf}
                onChange={(val) => setEndereco((prev) => ({ ...prev, uf: val }))}
                placeholder="UF"
                className="text-xs"
              />
            </div>
          </div>
        </div>

        {/* Rodapé de Ações */}
        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-hidden transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-hidden flex items-center space-x-1.5 shadow-xs transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                <span>Salvar Alterações</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
